// Static stage-duration table + ETA math.
//
// V1 uses a hard-coded per-stage baseline pulled from SCENEFORGE_OPTIMIZE.md
// post-mask-parallelization targets. Once we have ≥5 successful runs the
// Worker can expose a rolling average and this module's table becomes the
// fallback (see SCENEFORGE_OPTIMIZE.md §3j). Keep the math here pure — no
// React, no DOM, no fetch — so it stays trivially testable.

import type { StageKey, StageTimestampMap } from './types';

/** Pipeline stage order matters for ETA: stages downstream of the current one
 *  are still summed in full; the active stage is summed minus its already-
 *  elapsed slice. */
// Display order in the stepper. Reflects the LOGICAL flow the partner sees,
// not the wall-clock dispatch order. On the V3 DAG, `clean_plate` and
// `multi_mask` actually fire in parallel with `extract` and `environment`;
// we still show them in chronological READ order because that's how the
// partner thinks about "what's happening next".
// V4 Simple: replaces world_track/car_pose/track_compose with
// extract_clean_plate (post clean-plate-approval JPEG sequence) +
// feature_tracks_export (now the GPU SuperPoint+LightGlue stage on
// the car body). The V3 stages stay in STATIC_STAGE_DURATIONS_MS as
// historical-row budgets, but they're no longer in the active display
// order. See SCENEFORGE_SIMPLE_V4.md §9.3.
export const STAGE_ORDER: ReadonlyArray<StageKey> = [
  'extract',
  'clean_plate',
  'environment',
  'multi_mask',
  'extract_clean_plate',
  'feature_tracks_export',
  'hdri',
  'package',
];

/** Per-stage typical wall time in milliseconds. V4 Simple (SCENEFORGE_SIMPLE_V4.md §9.3):
 *  - extract:               extract_frames @ 1080p / 240 frames + reference_frames pre-roll
 *  - clean_plate:           Seedance 2.0 7-15 min typical for 10s clip @ 1080p (long pole)
 *  - environment:           Nano Banana 2 equirect at high thinking (~60s)
 *  - multi_mask:            SAM 3 multi-class car + wheels masks (~2 min)
 *  - extract_clean_plate:   ffmpeg-split the approved clean plate into JPEGs (~30 s)
 *  - feature_tracks_export: GPU SuperPoint+LightGlue on car body (~4 min)
 *  - hdri:                  Skybox queue + 8K JPG export (~4 min typical)
 *  - package:               zipfile + R2 upload incl. source/cp frames (~30 s)
 *
 *  Legacy V3 stages (world_track / car_pose / track_compose) are kept
 *  for historical row ETA display only — they're no longer in STAGE_ORDER. */
export const STATIC_STAGE_DURATIONS_MS: Record<StageKey, number> = {
  extract: 30_000,
  clean_plate: 900_000,
  environment: 60_000,
  multi_mask: 120_000,
  extract_clean_plate: 30_000,
  feature_tracks_export: 240_000,
  hdri: 240_000,
  package: 30_000,
  // Legacy V3 — kept for historical job ETA display, NOT in STAGE_ORDER.
  world_track: 180_000,
  car_pose: 120_000,
  track_compose: 30_000,
  // V2 alias — kept so existing rows whose ``current_stage`` is
  // ``car_track`` still get a budget assigned.
  car_track: 330_000,
};

/** Total typical wall time for a full run — used as the denominator when the
 *  job carries no `created_at` data yet. */
export const TOTAL_STATIC_DURATION_MS: number = STAGE_ORDER.reduce(
  (sum, k) => sum + STATIC_STAGE_DURATIONS_MS[k],
  0,
);

/** Sum the static durations of all stages NOT already in
 *  `stageCompletedAt`, then subtract the elapsed portion of the currently
 *  running stage (computed from `currentStagePercent` if provided, otherwise
 *  from the wall-clock delta against `currentStageStartedAt`). Result is
 *  floored at 0 — never returns a negative remainder. */
export function estimateRemainingMs(
  stageCompletedAt: StageTimestampMap | undefined,
  currentStage?: StageKey | null,
  currentStageStartedAt?: string | null,
  currentStagePercent?: number | null,
): number {
  const completed = stageCompletedAt ?? {};
  let remaining = 0;
  for (const stage of STAGE_ORDER) {
    if (completed[stage]) continue;
    remaining += STATIC_STAGE_DURATIONS_MS[stage];
  }

  if (currentStage && STATIC_STAGE_DURATIONS_MS[currentStage] !== undefined) {
    const stageBudget = STATIC_STAGE_DURATIONS_MS[currentStage];
    let elapsedInStage = 0;

    if (
      typeof currentStagePercent === 'number' &&
      Number.isFinite(currentStagePercent) &&
      currentStagePercent > 0
    ) {
      const pct = Math.min(100, Math.max(0, currentStagePercent));
      elapsedInStage = (pct / 100) * stageBudget;
    } else if (currentStageStartedAt) {
      const started = Date.parse(currentStageStartedAt);
      if (Number.isFinite(started)) {
        elapsedInStage = Math.max(0, Date.now() - started);
      }
    }

    remaining -= Math.min(elapsedInStage, stageBudget);
  }

  return Math.max(0, Math.round(remaining));
}

export interface EtaResult {
  /** ISO timestamp for the projected completion moment. */
  etaIso: string;
  /** Milliseconds from "now" until the projected completion. */
  remainingMs: number;
}

/** Projected completion = now + estimateRemainingMs(...). `jobStartedAt` is
 *  retained in the signature so a future rolling-history implementation can
 *  scale the per-stage budget against the user's actual pace so far. */
export function estimateETA(
  jobStartedAt: string,
  stageCompletedAt: StageTimestampMap | undefined,
  currentStage?: StageKey | null,
  currentStageStartedAt?: string | null,
  currentStagePercent?: number | null,
): EtaResult {
  // Reserved for future rolling-history scaling; reading the field keeps the
  // arg in the signature without an unused-binding lint warning.
  void jobStartedAt;
  const remainingMs = estimateRemainingMs(
    stageCompletedAt,
    currentStage ?? undefined,
    currentStageStartedAt ?? undefined,
    currentStagePercent ?? undefined,
  );
  const etaIso = new Date(Date.now() + remainingMs).toISOString();
  return { etaIso, remainingMs };
}

/** "12m 41s" / "1h 03m" / "0m 28s" — the compact form a CGI artist scans.
 *  Mirrors the format used by Nuke's render bar and the §3h timing summary.
 *  Hours roll into "Hh MMm" once we cross the 1-hour mark; otherwise we keep
 *  the seconds resolution so short stages still read sensibly. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0m 00s';
  const totalSeconds = Math.round(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) {
    return `${h}h ${String(m).padStart(2, '0')}m`;
  }
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

/** Same arithmetic as `formatDuration` but in the verbose Nuke-style with a
 *  forced minute prefix even when m === 0 (`"0m 28s"`). Exposed so the timing
 *  summary table can keep a fixed-width column without special-casing. */
export function formatStageDuration(ms: number): string {
  return formatDuration(ms);
}
